import { useState, useEffect, useCallback } from "react";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { IUser, ICreateUser, IUpdateUser, IUserFilters, IUseMasterUsersReturn } from "./user.types";

interface User {
  _id: string;
  profileImage?: string;
  fcm?: string;
  name: string;
  mobileNo: string;
  dob: string;
  email: string;
  address?: string;
  city?: string;
  pincode?: string;
  languageName?: string;
  courseName?: string;
  standard?: string;
  role: "STUDENT" | "ADMIN";
  likedTestServices?: string[];
  likedBooks?: string[];
  purchasedBooks?: string[];
  purchasedTests?: string[];
  otpDetails?: {
    otp?: string;
    otpCreatedAt?: string;
  };
  password?: string;
  bookMarkedArticle?: string[];
  isDemo: boolean;
  isDisabled: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateUserData {
  name: string;
  mobileNo: string;
  email: string;
  password: string;
  confirmPassword:string
}

interface UpdateUserData {
  name?: string;
  mobileNo?: string;
  email?: string;
  address?: string;
  city?: string;
  pincode?: string;
  languageName?: string;
  courseName?: string;
  standard?: string;
  isDisabled?: boolean;
  dob?: string;
  password?: string;
}

interface UserFilters {
  role?: "STUDENT" | "ADMIN";
  isDisabled?: boolean;
  fromDate?: string;
  toDate?: string;
  search?: string;
  standard?: string;
  courseName?: string;
  languageName?: string;
  isDemo?: boolean;
}

const useMasterUsers = () => {
  const [usersData, setUsersData] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilters>({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  });

  const navigate = useNavigate();

  // Initial fetch
  useEffect(() => {
    fetchUsers();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchUsersWithSearch(searchQuery);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const fetchUsers = useCallback(async (newFilters?: UserFilters) => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      const currentFilters = newFilters || filters;

      // Add filters to query params based on your schema
      if (currentFilters.role) queryParams.append('role', currentFilters.role);
      if (currentFilters.isDisabled !== undefined) queryParams.append('isDisabled', currentFilters.isDisabled.toString());
      if (currentFilters.isDemo !== undefined) queryParams.append('isDemo', currentFilters.isDemo.toString());
      if (currentFilters.fromDate) queryParams.append('fromDate', currentFilters.fromDate);
      if (currentFilters.toDate) queryParams.append('toDate', currentFilters.toDate);
      if (currentFilters.search) queryParams.append('search', currentFilters.search);
      if (currentFilters.standard) queryParams.append('standard', currentFilters.standard);
      if (currentFilters.courseName) queryParams.append('courseName', currentFilters.courseName);
      if (currentFilters.languageName) queryParams.append('languageName', currentFilters.languageName);
      
      queryParams.append('page', pagination.currentPage.toString());
      queryParams.append('limit', '10');

      const endpoint = queryParams.toString() 
        ? `${apiPaths.fetchMasterUsers}?${queryParams.toString()}`
        : apiPaths.fetchMasterUsers;

      const result = await ApiService({
        method: "GET",
        endpoint,
      });

      console.log('usersData : ', result);
      
      if (result.users) {
        setUsersData(result.users);
        setPagination({
          currentPage: result.currentPage || 1,
          totalPages: result.totalPages || 1,
          totalCount: result.totalCount || 0,
          hasNext: result.hasNext || false,
          hasPrev: result.hasPrev || false,
        });
      } else {
        setUsersData(Array.isArray(result) ? result : []);
      }

      if (newFilters) {
        setFilters(newFilters);
      }
    } catch (error: any) {
      console.error("Error fetching users:", error);
      setError("Error fetching users");
      toast.error("Error fetching users");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage]);

  const fetchUsersWithSearch = async (search?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      let endpoint = apiPaths.fetchMasterUsers;
      
      // Add search query parameter if provided
      if (search && search.trim().length > 0) {
        endpoint = `${apiPaths.fetchMasterUsers}?q=${encodeURIComponent(search.trim())}`;
      }

      const result = await ApiService({
        method: "GET",
        endpoint,
      });

      console.log('Search results: ', result);
      
      if (result.users) {
        setUsersData(result.users);
      } else {
        setUsersData(Array.isArray(result) ? result : []);
      }
    } catch (error: any) {
      console.error("Error searching users:", error);
      setError("Error searching users");
      toast.error("Error searching users");
    } finally {
      setLoading(false);
    }
  };

  const formatDateToISODateString = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const handleDateFilter = async (fromDate: Date, toDate: Date) => {
    const formattedFromDate = formatDateToISODateString(fromDate);
    const formattedToDate = formatDateToISODateString(toDate);

    await fetchUsers({
      ...filters,
      fromDate: formattedFromDate,
      toDate: formattedToDate,
    });
  };

  const searchUsers = async (query: string) => {
    await fetchUsers({
      ...filters,
      search: query,
    });
  };

  const filterByRole = async (role: "STUDENT" | "ADMIN" | null) => {
    await fetchUsers({
      ...filters,
      role: role || undefined,
    });
  };

  const filterByStatus = async (isDisabled: boolean | null) => {
    await fetchUsers({
      ...filters,
      isDisabled: isDisabled !== null ? isDisabled : undefined,
    });
  };

  const filterByDemo = async (isDemo: boolean | null) => {
    await fetchUsers({
      ...filters,
      isDemo: isDemo !== null ? isDemo : undefined,
    });
  };

  const resetFilters = async () => {
    setFilters({});
    await fetchUsers({});
  };

  const handleEdit = (id: string) => {
    setSelectedUserId(id);
  };

  const handleDelete = async (id: string) => {
 

    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.disableMasterUser}/${id}`,
      });
      
      setUsersData((prevData) =>
        prevData.filter((user) => user._id !== id)
      );
      
      toast.success("User deleted successfully");
      
      // Refresh the list to update pagination
      await fetchUsers();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      const errorMessage = error.response?.data?.message || "Error deleting user";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id: string, isDisabled: boolean) => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "PUT",
        endpoint:  !isDisabled ?  `${apiPaths.enableMasterUser}/${id}` : `${apiPaths.disableMasterUser}/${id}`,
        data: { isDisabled },
      });

      setUsersData((prevData) =>
        prevData.map((user) =>
          user._id === id ? { ...user, isDisabled } : user
        )
      );
      
      toast.success(`User ${isDisabled ? 'disabled' : 'enabled'} successfully`);
      return result;
    } catch (error: any) {
      console.error("Error updating user status:", error);
      const errorMessage = error.response?.data?.message || "Error updating user status";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    // Since you're using modal, we don't navigate
    // This can be used to open the modal from the hook if needed
    console.log("Add user action triggered");
  };

  const addUser = async (userData: CreateUserData): Promise<User> => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "POST",
        endpoint: apiPaths.createMasterUser,
        data: {
          ...userData,
          dob: new Date(), // You might want to make this required in the form
        },
      });
      
      // Add to local state
      setUsersData((prevData) => [result, ...prevData]);
      
      toast.success("User created successfully");
      
      // Refresh the list to get updated data
      await fetchUsers();
      
      return result;
    } catch (error: any) {
      console.error("Error creating user:", error);
      const errorMessage = error.response?.data?.message || "Error creating user";
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (id: string, userData: UpdateUserData): Promise<User> => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.createMasterUser}/${id}`,
        data: userData,
      });
      
      setUsersData((prevData) =>
        prevData.map((user) =>
          user._id === id ? { ...user, ...result } : user
        )
      );
      
      toast.success("User updated successfully");
      return result;
    } catch (error: any) {
      console.error("Error updating user:", error);
      const errorMessage = error.response?.data?.message || "Error updating user";
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getUserById = (id: string): User | undefined => {
    return usersData.find(user => user._id === id);
  };

  const clearSelectedUser = () => {
    setSelectedUserId(null);
  };

  // Additional utility functions for your specific schema
  const getAdminUsers = () => {
    return usersData.filter(user => user.role === 'ADMIN');
  };

  const getStudentUsers = () => {
    return usersData.filter(user => user.role === 'STUDENT');
  };

  const getActiveUsers = () => {
    return usersData.filter(user => !user.isDisabled);
  };

  const getDemoUsers = () => {
    return usersData.filter(user => user.isDemo);
  };

  const getUserStats = () => {
    return {
      total: usersData.length,
      active: getActiveUsers().length,
      disabled: usersData.filter(user => user.isDisabled).length,
      admins: getAdminUsers().length,
      students: getStudentUsers().length,
      demo: getDemoUsers().length,
    };
  };

  // Bulk operations for admin efficiency
  const bulkStatusUpdate = async (userIds: string[], isDisabled: boolean) => {
    if (!window.confirm(`Are you sure you want to ${isDisabled ? 'disable' : 'enable'} ${userIds.length} users?`)) {
      return;
    }

    setLoading(true);
    try {
      // If your API supports bulk operations
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateUser}/bulk-status`,
        data: { userIds, isDisabled },
      });
      
      await fetchUsers();
      toast.success(`Users ${isDisabled ? 'disabled' : 'enabled'} successfully`);
    } catch (error: any) {
      // Fallback to individual updates if bulk is not supported
      console.log("Bulk operation not supported, updating individually...");
      let successCount = 0;
      for (const userId of userIds) {
        try {
          await handleStatusUpdate(userId, isDisabled);
          successCount++;
        } catch (err) {
          console.error(`Failed to update user ${userId}:`, err);
        }
      }
      toast.success(`${successCount} users updated successfully`);
    } finally {
      setLoading(false);
    }
  };

  return {
    selectedUserId,
    usersData,
    loading,
    error,
    filters,
    pagination,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUpdate,
    handleDateFilter,
    handleAdd,
    addUser,
    updateUser,
    getUserById,
    clearSelectedUser,
    fetchUsers,
    searchUsers,
    filterByRole,
    filterByStatus,
    filterByDemo,
    resetFilters,
    // Additional utility functions
    getAdminUsers,
    getStudentUsers,
    getActiveUsers,
    getDemoUsers,
    getUserStats,
    bulkStatusUpdate,
  };
};

export default useMasterUsers;